import { formatSecondsToHHMMSS } from "@utils/utils";
import { format, parseISO } from "date-fns";
import React, { useEffect, useState } from "react";

import { CalendarDateRange, Column, SearchableDropdownItem } from "@fronterahealth/frontera-ui-components";

import { useGetTotalVideoDurationRawDataQuery } from "@api/graphql/types-and-hooks";
import { DialogFilterBar } from "@pages/VideoAnalytics/components/DialogFilterBar";
import { RawDataTableWrapper } from "@pages/VideoAnalytics/components/RawDataTableWrapper";
import { DATE_FORMAT, PAGE_SIZE } from "@pages/VideoAnalytics/helpers";
import { useVideoAnalytics } from "@providers/VideoAnalyticsProvider";

interface VideoRecordingDurationRawDataProps {}

interface VideoRecordingDurationRawDataType {
  rbtName: string;
  clientName: string;
  sessionDate: string;
  sessionDatetime: string;
  sessionDurationInSeconds: number;
}
export const VideoRecordingDurationRawData: React.FC<VideoRecordingDurationRawDataProps> = () => {
  const { selectedRBTDates, selectedBCBA: selectedBCBAList } = useVideoAnalytics();

  const [selectedRBT, setSelectedRBT] = useState<SearchableDropdownItem[]>(selectedBCBAList);
  const [dates, setDates] = useState<CalendarDateRange | undefined>(selectedRBTDates);
  const [selectedClient, setSelectedClient] = useState<SearchableDropdownItem[]>([]);

  const [pageNo, setPageNo] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(PAGE_SIZE);

  useEffect(() => {
    setPageNo(1);
  }, [dates, selectedRBT, selectedClient]);

  const { data, isFetching } = useGetTotalVideoDurationRawDataQuery(
    {
      fromDate: dates?.startDate ? format(dates.startDate, DATE_FORMAT) : format(new Date(), DATE_FORMAT),
      toDate: dates?.endDate ? format(dates.endDate, DATE_FORMAT) : format(new Date(), DATE_FORMAT),
      rbtProviderIds: selectedRBT.map((item) => item.id),
      learnerIds: selectedClient.map((item) => item.id),
      offset: pageNo * pageSize - pageSize,
      count: pageSize,
    },
    {
      queryKey: ["getTotalVideoDurationRawData", dates, selectedRBT, pageNo, pageSize, selectedClient],
    },
  );
  const columns: Column<VideoRecordingDurationRawDataType>[] = [
    {
      title: "Client",
      key: "clientName",
      dataIndex: "clientName",
      render: (value) => <span className="ph-no-capture">{(value as string) || "-"}</span>,
    },

    {
      title: "Session Date & Time",
      key: "sessionDatetime",
      dataIndex: "sessionDatetime",
      render: (value) => {
        if (value) {
          const parsedDate = parseISO(value as string);
          const formattedDate = format(parsedDate, "yyyy-MM-dd HH:mm");
          return formattedDate;
        }
        return "-";
      },
    },
    {
      title: "Session Duration",
      key: "sessionDurationInSeconds",
      dataIndex: "sessionDurationInSeconds",
      render: (value: string | number) => (value ? formatSecondsToHHMMSS(value as number) : "-"),
    },
    {
      title: "RBT",
      key: "rbtName",
      dataIndex: "rbtName",
      render: (value) => value || "-",
    },
  ];
  return (
    <div className="grid space-y-2">
      <DialogFilterBar
        dates={dates}
        setDates={setDates}
        selectedRBTs={selectedRBT}
        setSelectedRBTs={setSelectedRBT}
        selectedClients={selectedClient}
        setSelectedClients={setSelectedClient}
        showBCBAFilter={false}
      />
      <RawDataTableWrapper
        data={
          data && data.getTotalVideoDurationRawData?.rawData
            ? (data.getTotalVideoDurationRawData?.rawData as VideoRecordingDurationRawDataType[])
            : []
        }
        columns={columns}
        loading={isFetching}
        totalItemCount={data?.getTotalVideoDurationRawData?.totalRecordsCount || 0}
        defaultItemsPerPage={pageSize}
        currentPage={pageNo}
        onPageChange={(current, itemsPerPage) => {
          setPageNo(current);
          setPageSize(itemsPerPage);
        }}
      />
    </div>
  );
};
